import { type IllustrationType } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Button } from "@src/appV2/redesign/components/Button";
import { DocumentStatusPill } from "@src/appV2/redesign/Documents/components/StatusPill";
import { WORKPLACE_DOCUMENTS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import pluralize from "pluralize";
import { generatePath, useHistory } from "react-router-dom";

import { QuickSignPill } from "../../Bookability/Documents/InstantReview/QuickSignPill";
import { type BlockingRequirementsContext } from "../../Bookability/Documents/types";
import { ShiftBottomSheetBookabilityCriteriaCard } from "../BookabilityCriteriaCard";

interface ShiftBottomSheetGroupedBlockingRequirementsCardProps {
  blockingRequirementsContext: BlockingRequirementsContext;
  shiftId: string;
  qualification?: string;
  workplaceId: string;
}

export function ShiftBottomSheetGroupedBlockingRequirementsCard(
  props: ShiftBottomSheetGroupedBlockingRequirementsCardProps
) {
  const { blockingRequirementsContext, shiftId, qualification, workplaceId } = props;
  const { navigateToModal } = useShiftModalsDataContext();
  const history = useHistory();

  const {
    areAllVisibleBlockedDocumentsInstantReview,
    visibleBlockingRequirements,
    areAllVisibleBlockedDocumentsInReview,
    notInReviewVisibleBlockingRequirements,
  } = blockingRequirementsContext;

  function getPill() {
    if (areAllVisibleBlockedDocumentsInReview) {
      return <DocumentStatusPill variant="in_review" />;
    }

    if (areAllVisibleBlockedDocumentsInstantReview) {
      return <QuickSignPill />;
    }

    return null;
  }

  function getIcon(): IllustrationType {
    if (areAllVisibleBlockedDocumentsInReview) {
      return "document-review";
    }

    if (areAllVisibleBlockedDocumentsInstantReview) {
      return "quiz-required";
    }

    return "document-upload";
  }

  function getTitle() {
    if (areAllVisibleBlockedDocumentsInReview) {
      const numberOfDocumentsInReview = visibleBlockingRequirements.length;
      return `The ${numberOfDocumentsInReview} ${pluralize(
        "document",
        numberOfDocumentsInReview
      )} you've submitted are being reviewed by our team`;
    }

    const count = notInReviewVisibleBlockingRequirements.length;
    return `Complete ${count} additional ${pluralize("document", count)} to book this shift`;
  }

  function onClick() {
    if (areAllVisibleBlockedDocumentsInstantReview) {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH, {
        pathParams: {
          requirementId: visibleBlockingRequirements[0].requirementId,
          shiftId,
        },
        forceReplacePath: true,
      });
      return;
    }

    const searchParams = new URLSearchParams();
    if (isDefined(qualification)) {
      searchParams.set("qualification", qualification);
    }

    searchParams.set("shiftId", shiftId);
    const path = generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, {
      workplaceId,
    });
    history.push(`${path}?${searchParams.toString()}`);
  }

  return (
    <ShiftBottomSheetBookabilityCriteriaCard
      title={getTitle()}
      illustrationType={getIcon()}
      pill={getPill()}
      footer={
        <Button
          fullWidth
          variant={areAllVisibleBlockedDocumentsInReview ? "outlined" : "contained"}
          size="medium"
          onClick={() => {
            onClick();
          }}
        >
          Complete Documents
        </Button>
      }
    />
  );
}
